import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Scene3D } from '../../three/Scene3D';
import { HeroActions } from './HeroActions';
import { HeroBackground } from './HeroBackground';
import { buildHeroSlides } from './hero.slides';
import type { HeroContent } from './hero.types';

const EASE = [0.16, 1, 0.3, 1] as const;
/** How long each slide holds before auto-advancing. */
const HOLD_MS = 7000;

/** Sequenced load-in for one slide's copy column. */
const copyGroup = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
};
const copyItem = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

/** Wraps the highlighted phrase in the animated brand sheen, kept readable. */
function Headline({ text, highlight }: { text: string; highlight?: string }): JSX.Element {
  if (!highlight || !text.includes(highlight)) return <>{text}</>;
  const [before, after] = text.split(highlight);
  return (
    <>
      {before}
      <span className="hero-shine" data-hero-loop>
        {highlight}
      </span>
      {after}
    </>
  );
}

/**
 * A full-viewport, auto-rotating hero reel: each slide fills the screen with a
 * large centred headline, two CTAs, and a drifting brand wash. Dots + arrow keys
 * + swipe navigate; auto-rotation pauses on hover/focus and under reduced motion.
 * Layout pattern borrowed from full-screen product heroes — content is Vaanexa's.
 */
export function HeroCarousel({ content }: { content: HeroContent }): JSX.Element {
  const reduce = useReducedMotion();
  const slides = useMemo(() => buildHeroSlides(content), [content]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const dragX = useRef(0);
  // The 3D layer reads its scroll progress from this element's box.
  const stage = useRef<HTMLElement>(null);

  const go = useCallback(
    (next: number) => setIndex(((next % slides.length) + slides.length) % slides.length),
    [slides.length],
  );

  // Auto-advance unless paused or reduced-motion. Reset the timer whenever the
  // active slide or pause state changes.
  useEffect(() => {
    if (paused || reduce) return;
    const timer = window.setTimeout(() => go(index + 1), HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [index, paused, reduce, go]);

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowRight') go(index + 1);
    else if (e.key === 'ArrowLeft') go(index - 1);
  };

  const slide = slides[index]!;

  return (
    <section
      ref={stage}
      className="hero-dark relative isolate overflow-hidden"
      aria-roledescription="carousel"
      aria-label="Vaanexa highlights"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onKeyDown={onKeyDown}
      tabIndex={-1}
    >
      <HeroBackground />

      {/* WebGL depth on top of the CSS stage, under all the copy. Renders nothing
          on devices without WebGL or with reduced-motion asked for — the CSS
          background below is a complete design by itself. */}
      <Scene3D target={stage} />

      {/* Per-slide tint wash, crossfaded above the shared stage. */}
      <AnimatePresence mode="sync">
        <motion.div
          key={`tint-${slide.id}`}
          className="pointer-events-none absolute inset-0"
          style={{ background: slide.tint }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          aria-hidden
        />
      </AnimatePresence>

      <div className="relative mx-auto flex min-h-[88svh] w-full max-w-[1100px] items-center justify-center px-5 py-24 sm:px-8">
        {/* Slides are stacked and crossfade — both are mounted during the swap,
            so there is never an empty frame between them. */}
        <AnimatePresence initial={false}>
          <motion.div
            key={slide.id}
            variants={reduce ? undefined : copyGroup}
            initial="hidden"
            animate="show"
            exit={{ opacity: 0, transition: { duration: 0.6, ease: EASE } }}
            drag={reduce ? false : 'x'}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.15}
            onDragStart={(_, info) => (dragX.current = info.point.x)}
            onDragEnd={(_, info) => {
              const dx = info.point.x - dragX.current;
              if (dx < -60) go(index + 1);
              else if (dx > 60) go(index - 1);
            }}
            className="absolute inset-0 flex cursor-grab flex-col items-center justify-center gap-6 px-5 text-center active:cursor-grabbing sm:px-8"
            role="group"
            aria-roledescription="slide"
            aria-label={`${index + 1} of ${slides.length}`}
          >
            {slide.eyebrow ? (
              <motion.p
                variants={reduce ? undefined : copyItem}
                className="inline-flex items-center gap-2 rounded-full border border-[var(--hero-border)] bg-[var(--hero-glass)] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--hero-text-muted)] shadow-[inset_0_1px_0_rgba(255,255,255,0.4)] backdrop-blur-sm"
              >
                <span
                  className="h-1.5 w-1.5 rounded-full bg-[var(--hero-glow-accent)] shadow-[0_0_8px_var(--hero-glow-accent)]"
                  aria-hidden
                />
                {slide.eyebrow}
              </motion.p>
            ) : null}

            <motion.h1
              variants={reduce ? undefined : copyItem}
              className="hero-headline max-w-[16ch] font-[var(--font-display)] font-semibold text-[var(--hero-text)]"
            >
              <Headline text={slide.headline} highlight={slide.highlight} />
            </motion.h1>

            <motion.p
              variants={reduce ? undefined : copyItem}
              className="max-w-xl text-[1.0625rem] leading-relaxed text-[var(--hero-text-soft)] sm:text-lg"
            >
              {slide.description}
            </motion.p>

            <motion.div variants={reduce ? undefined : copyItem} className="mt-2">
              <HeroActions
                primary={slide.primaryCta}
                secondary={slide.secondaryCta ?? slide.primaryCta}
              />
            </motion.div>

            <motion.p
              variants={reduce ? undefined : copyItem}
              className="inline-flex items-center gap-2 text-sm text-[var(--hero-text-muted)]"
            >
              <ShieldCheck className="h-4 w-4 text-[var(--hero-glow-accent)]" aria-hidden />
              {content.trustText}
            </motion.p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Dot navigation — active dot stretches into a pill and fills as the
          auto-rotation timer runs. */}
      <div className="absolute inset-x-0 bottom-8 z-10 flex items-center justify-center gap-2.5">
        {slides.map((s, i) => {
          const active = i === index;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => go(i)}
              aria-label={`Go to slide ${i + 1}: ${s.headline}`}
              aria-current={active}
              className="group relative h-2 overflow-hidden rounded-full transition-all duration-500"
              style={{ width: active ? 40 : 8 }}
            >
              <span
                className="absolute inset-0 rounded-full bg-[var(--hero-border-strong)]"
                aria-hidden
              />
              {active ? (
                paused || reduce ? (
                  <span
                    className="absolute inset-0 rounded-full bg-[var(--hero-glow-primary)]"
                    aria-hidden
                  />
                ) : (
                  <motion.span
                    key={`fill-${index}`}
                    className="absolute inset-y-0 left-0 rounded-full bg-[var(--hero-glow-primary)]"
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: HOLD_MS / 1000, ease: 'linear' }}
                    aria-hidden
                  />
                )
              ) : (
                <span
                  className="absolute inset-0 rounded-full bg-[var(--hero-text-muted)] opacity-0 transition-opacity group-hover:opacity-40"
                  aria-hidden
                />
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}
